import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, Grid, Chip, CircularProgress,
    IconButton, TextField, Alert, Avatar,
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Visibility as EyeIcon,
    VisibilityOff as EyeOffIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import { driversAPI, carTypesAPI, agreementsAPI } from '../services/apiService';
import { useAlert } from '../contexts/AlertContext';
import { useSettings } from '../contexts/SettingsContext';
import { formatDate } from '../utils/dateHelpers';

const DOCUMENT_TYPES = [
    { key: 'ProfilePhoto', label: 'Profile Photo' },
    { key: 'LicensePhoto', label: 'License Photo' },
    { key: 'InsurancePhoto', label: 'Insurance Card' },
    { key: 'RegistrationPhoto', label: 'Registration' },
    { key: 'ChauffeurLicensePhoto', label: 'Chauffeur License' },
    { key: 'CarPhoto', label: 'Car Photo' },
];

const InfoRow = ({ label, value }) => (
    value ? (
        <Box mb={1}>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            <Typography variant="body2">{value}</Typography>
        </Box>
    ) : null
);

const SectionTitle = ({ children }) => (
    <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mt: 2, mb: 1 }}>
        {children}
    </Typography>
);

export default function PendingDriverDetailModal({ driverId, open, onClose, onAction }) {
    const { showAlert, showToast } = useAlert();
    const { settings } = useSettings();
    const [driver, setDriver] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [ssnRevealed, setSsnRevealed] = useState(null);
    const [ssnLoading, setSsnLoading] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [carTypes, setCarTypes] = useState([]);
    const [signedAgreements, setSignedAgreements] = useState([]);

    useEffect(() => {
        carTypesAPI.getAll().then(types => setCarTypes(types || [])).catch(() => { });
    }, []);

    useEffect(() => {
        if (!open || !driverId) return;
        setLoading(true);
        setSignedAgreements([]);
        driversAPI.getApplicationById(driverId)
            .then(d => setDriver(d))
            .catch(() => showToast('Failed to load application details', 'error'))
            .finally(() => setLoading(false));
        agreementsAPI.getSignedForDriver(driverId)
            .then(sigs => setSignedAgreements(sigs || []))
            .catch(() => { });
    }, [driverId, open]);

    const handleApprove = () => {
        showAlert('Approve Driver', `Approve ${driver?.name || 'this driver'}? Login credentials will be generated and emailed to them.`, [
            { label: 'Cancel', color: 'inherit' },
            {
                label: 'Approve', color: 'success', onClick: async () => {
                    setActionLoading(true);
                    try {
                        const result = await driversAPI.approveApplication(driverId);
                        if (result?.emailSent === false) {
                            const u = result?.generatedUsername || '(unknown)';
                            const p = result?.generatedPassword || '(unknown)';
                            showAlert(
                                'Driver Approved — Email Failed',
                                `The driver was approved, but the credentials email could NOT be sent. Please share these credentials with the driver manually:\n\nUsername: ${u}\nPassword: ${p}\n\n(Check the server logs / SMTP configuration to fix email delivery.)`,
                                [{ label: 'OK', color: 'primary' }],
                                'warning'
                            );
                        } else {
                            showToast('Driver approved! Credentials sent.', 'success');
                        }
                        onAction?.();
                    } catch (err) {
                        showToast(err?.response?.data?.error || 'Approval failed', 'error');
                    } finally {
                        setActionLoading(false);
                    }
                }
            },
        ]);
    };

    const handleReject = async () => {
        setActionLoading(true);
        try {
            await driversAPI.rejectApplication(driverId, rejectReason);
            showToast('Application rejected', 'success');
            setRejectDialogOpen(false);
            onAction?.();
        } catch (err) {
            showToast(err?.response?.data?.error || 'Rejection failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRevealSsn = async () => {
        if (ssnRevealed) { setSsnRevealed(null); return; }
        setSsnLoading(true);
        try {
            const res = await driversAPI.revealSsn(driverId);
            setSsnRevealed(res.ssn);
        } catch {
            showToast('Failed to reveal SSN', 'error');
        } finally {
            setSsnLoading(false);
        }
    };

    const documentUrl = (type) => driver?.documents?.[type] ?? null;

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={700}>Driver Application</Typography>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
                ) : !driver ? (
                    <Alert severity="error">Could not load application.</Alert>
                ) : (
                    <Grid container spacing={3}>
                        {/* Column 1 — Personal */}
                        <Grid item xs={12} md={4}>
                            {driver.documents?.ProfilePhoto && (
                                <Box mb={2} display="flex" justifyContent="center">
                                    <Avatar
                                        src={documentUrl('ProfilePhoto')}
                                        sx={{ width: 100, height: 100, cursor: 'pointer', border: '2px solid', borderColor: 'primary.main' }}
                                        onClick={() => setSelectedPhoto(documentUrl('ProfilePhoto'))}
                                    />
                                </Box>
                            )}

                            <SectionTitle>Personal Information</SectionTitle>
                            <InfoRow label="Name" value={driver.name} />
                            <InfoRow label="Email" value={driver.email} />
                            <InfoRow label="Phone" value={driver.phoneNumber} />
                            <InfoRow label="Date of Birth" value={driver.dateOfBirth ? formatDate(driver.dateOfBirth) : ''} />
                            <InfoRow label="Gender" value={driver.gender} />

                            {driver.ssnLast4 && (
                                <Box mb={1}>
                                    <Typography variant="caption" color="text.secondary" display="block">SSN</Typography>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2" fontFamily="monospace">
                                            {ssnRevealed ? ssnRevealed : `***-**-${driver.ssnLast4}`}
                                        </Typography>
                                        <IconButton size="small" onClick={handleRevealSsn} disabled={ssnLoading}>
                                            {ssnLoading ? <CircularProgress size={14} /> : ssnRevealed ? <EyeOffIcon fontSize="small" /> : <EyeIcon fontSize="small" />}
                                        </IconButton>
                                    </Box>
                                </Box>
                            )}

                            {(driver.addressStreet || driver.addressCity) && (
                                <>
                                    <SectionTitle>Address</SectionTitle>
                                    <InfoRow label="Street" value={driver.addressStreet} />
                                    <InfoRow label="City" value={driver.addressCity} />
                                    <InfoRow label="State / Zip" value={[driver.addressState, driver.addressZip].filter(Boolean).join(' ')} />
                                </>
                            )}
                        </Grid>

                        {/* Column 2 — License / Insurance / Vehicle */}
                        <Grid item xs={12} md={4}>
                            {(driver.emergencyContactName || driver.emergencyContactPhone) && (
                                <>
                                    <SectionTitle>Emergency Contact</SectionTitle>
                                    <InfoRow label="Name" value={driver.emergencyContactName} />
                                    <InfoRow label="Phone" value={driver.emergencyContactPhone} />
                                </>
                            )}

                            {(driver.yearsDrivingExperience != null || driver.languagesSpoken || driver.preferredWorkHours) && (
                                <>
                                    <SectionTitle>Additional</SectionTitle>
                                    <InfoRow label="Years Experience" value={driver.yearsDrivingExperience?.toString()} />
                                    <InfoRow label="Languages" value={driver.languagesSpoken} />
                                    <InfoRow label="Preferred Hours" value={driver.preferredWorkHours} />
                                </>
                            )}



                            {(driver.licenseNumber || driver.licenseState || driver.chauffeurLicenseNumber) && (
                                <>
                                    <SectionTitle>Driver's License</SectionTitle>
                                    <InfoRow label="License #" value={driver.licenseNumber} />
                                    <InfoRow label="State" value={driver.licenseState} />
                                    <InfoRow label="Expires" value={formatDate(driver.licenseExpiration)} />
                                    <InfoRow label="Chauffeur License #" value={driver.chauffeurLicenseNumber} />
                                </>
                            )}

                            {(driver.insuranceCarrier || driver.insurancePolicyNumber) && (
                                <>
                                    <SectionTitle>Insurance</SectionTitle>
                                    <InfoRow label="Carrier" value={driver.insuranceCarrier} />
                                    <InfoRow label="Policy #" value={driver.insurancePolicyNumber} />
                                    <InfoRow label="Expires" value={formatDate(driver.insuranceExpiration)} />
                                </>
                            )}
                        </Grid>

                        {/* Column 3 — Documents & Application Info */}
                        <Grid item xs={12} md={4}>
                            {driver.pendingCar && (
                                <>
                                    <SectionTitle>Pending Vehicle</SectionTitle>
                                    <InfoRow label="Type" value={carTypes.find(ct => ct.id === driver.pendingCar.type)?.name ?? driver.pendingCar.type?.toString()} />
                                    <InfoRow label="Make" value={driver.pendingCar.make} />
                                    <InfoRow label="Model" value={driver.pendingCar.model} />
                                    <InfoRow label="Year" value={driver.pendingCar.year?.toString()} />
                                    <InfoRow label="Color" value={driver.pendingCar.color} />
                                    <InfoRow label="License Plate" value={driver.pendingCar.licensePlate} />
                                    <InfoRow label="Seats" value={driver.pendingCar.seats?.toString()} />
                                </>
                            )}

                            {driver.documents && Object.keys(driver.documents).length > 0 && (
                                <>
                                    <SectionTitle>Documents</SectionTitle>
                                    <Box display="flex" flexWrap="wrap" gap={1}>
                                        {DOCUMENT_TYPES.filter(dt => driver.documents[dt.key]).map(dt => (
                                            <Box
                                                key={dt.key}
                                                component="img"
                                                src={documentUrl(dt.key)}
                                                alt={dt.label}
                                                title={dt.label}
                                                sx={{
                                                    width: 100, height: 80, objectFit: 'cover', borderRadius: 1,
                                                    border: '1px solid', borderColor: 'divider', cursor: 'pointer'
                                                }}
                                                onClick={() => setSelectedPhoto(documentUrl(dt.key))}
                                            />
                                        ))}
                                    </Box>
                                </>
                            )}

                            <SectionTitle>Application Info</SectionTitle>
                            <Box display="flex" gap={3}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">Submitted</Typography>
                                    <Typography variant="body2">{formatDate(driver.applicationSubmittedUtc) ?? '—'}</Typography>
                                </Box>
                                {driver.applicationTokenExpiry && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">Invited</Typography>
                                        <Typography variant="body2">{formatDate(
                                            new Date(new Date(driver.applicationTokenExpiry).getTime() - (settings?.applicationTokenLifetimeDays ?? 7) * 86400000).toISOString()
                                        )}</Typography>
                                    </Box>
                                )}
                            </Box>

                            {driver.backgroundCheckConsent && (
                                <Chip label="✓ Background Check Consent" color="success" size="small" sx={{ mt: 1 }} />
                            )}

                            {signedAgreements.length > 0 && (
                                <>
                                    <SectionTitle>Signed Agreements</SectionTitle>
                                    <Box display="flex" flexDirection="column" gap={1}>
                                        {signedAgreements.map(sig => (
                                            <Box key={sig.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.5, py: 0.5 }}>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>{sig.titleSnapshot}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Signed by {sig.signerName} · {new Date(sig.signedUtc).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                                <IconButton size="small" title="Download signed PDF"
                                                    onClick={async () => {
                                                        try {
                                                            const blob = await agreementsAPI.downloadSigned(driverId, sig.id);
                                                            const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                                                            const link = document.createElement('a');
                                                            link.href = url;
                                                            link.setAttribute('download', `Signed_${sig.titleSnapshot}.pdf`);
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            link.remove();
                                                            window.URL.revokeObjectURL(url);
                                                        } catch { showToast('Download failed', 'error'); }
                                                    }}>
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                </>
                            )}
                        </Grid>
                    </Grid>
                )}

            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={onClose} color="inherit">Close</Button>
                <Button
                    startIcon={<RejectIcon />}
                    color="error"
                    variant="outlined"
                    onClick={() => { setRejectReason(''); setRejectDialogOpen(true); }}
                    disabled={actionLoading || !driver || driver.approvedUtc || driver.rejectedUtc}
                >
                    Reject
                </Button>
                <Button
                    startIcon={<ApproveIcon />}
                    color="success"
                    variant="contained"
                    onClick={handleApprove}
                    disabled={actionLoading || !driver || driver.approvedUtc || driver.rejectedUtc}
                >
                    {actionLoading ? <CircularProgress size={18} /> : 'Approve'}
                </Button>
            </DialogActions>

            {/* Rejection confirmation popup */}
            <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm Rejection</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Optionally provide a reason — it will be included in the email sent to the applicant.
                    </Typography>
                    <TextField
                        label="Reason (optional)"
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialogOpen(false)} color="inherit">Cancel</Button>
                    <Button
                        startIcon={<RejectIcon />}
                        color="error"
                        variant="contained"
                        onClick={handleReject}
                        disabled={actionLoading}
                    >
                        {actionLoading ? <CircularProgress size={18} /> : 'Confirm Rejection'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Full-size photo lightbox */}
            {selectedPhoto && (
                <Dialog open onClose={() => setSelectedPhoto(null)} maxWidth="lg">
                    <DialogContent sx={{ p: 0 }}>
                        <Box
                            component="img"
                            src={selectedPhoto}
                            alt="Document"
                            sx={{ maxWidth: '90vw', maxHeight: '90vh', display: 'block' }}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </Dialog>
    );
}
